import React, { useState, useEffect } from "react";
import { Form, Button, Table, Modal } from "react-bootstrap";
import ConfirmationModal from "./Modal/ConfirmationModal";

const LeadManagement = () => {
  const [leads, setLeads] = useState([]);
  const [newLead, setNewLead] = useState({
    name: "",
    email: "",
    source: "",
    status: "New",
    followUpDate: "",
  });
  const [errors, setErrors] = useState({}); // State to manage validation errors
  const [showModal, setShowModal] = useState(false); // State for confirmation modal
  const [leadToDelete, setLeadToDelete] = useState(null); // Lead selected for deletion
  const [leadToEdit, setLeadToEdit] = useState(null); // Lead selected for editing

  useEffect(() => {
    // Fetch leads from an API or a mock data source
    setLeads([
      {
        id: 1,
        name: "John Doe",
        email: "john.doe@example.com",
        source: "Social Media",
        status: "Contacted",
        followUpDate: "2024-12-15",
      },
      {
        id: 2,
        name: "Jane Smith",
        email: "jane.smith@example.com",
        source: "Google Ads",
        status: "Interested",
        followUpDate: "2024-12-18",
      },
    ]);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewLead({ ...newLead, [name]: value });
    setErrors({ ...errors, [name]: "" }); // Clear the error for the field being edited
  };

  const validateNewLead = () => {
    const newErrors = {};
    if (!newLead.name.trim()) {
      newErrors.name = "Name is required.";
    }
    if (!newLead.email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(newLead.email)) {
      newErrors.email = "Enter a valid email address.";
    }
    if (!newLead.source.trim()) {
      newErrors.source = "Source is required.";
    }
    if (!newLead.followUpDate) {
      newErrors.followUpDate = "Follow-Up Date is required.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // Return true if no errors
  };

  const addLead = () => {
    if (validateNewLead()) {
      if (leadToEdit) {
        // Edit existing lead
        const updatedLeads = leads.map((lead) =>
          lead.id === leadToEdit.id ? { ...lead, ...newLead } : lead
        );
        setLeads(updatedLeads);
        setLeadToEdit(null);
      } else {
        // Add new lead
        const updatedLeads = [...leads, { id: leads.length + 1, ...newLead }];
        setLeads(updatedLeads);
      }
      setNewLead({
        name: "",
        email: "",
        source: "",
        status: "New",
        followUpDate: "",
      });
      setErrors({}); // Clear errors after successful addition
    }
  };

  const handleEdit = (lead) => {
    setNewLead(lead);
    setLeadToEdit(lead);
  };

  const confirmDelete = (lead) => {
    setLeadToDelete(lead);
    setShowModal(true);
  };

  const handleDelete = () => {
    const updatedLeads = leads.filter((lead) => lead.id !== leadToDelete.id);
    setLeads(updatedLeads);
    setLeadToDelete(null);
    setShowModal(false);
  };

  const closeModal = () => {
    setLeadToDelete(null);
    setShowModal(false);
  };

  return (
    <div className="container mt-4">
      <h2 className="mb-4">Lead and Opportunity Management</h2>

      {/* Add/Edit Lead */}
      <div className="mb-4">
        <h4>{leadToEdit ? "Edit Lead" : "Add New Lead"}</h4>
        <Form>
          <div className="row g-3">
            <div className="col-md-3">
              <Form.Group controlId="leadName">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={newLead.name}
                  onChange={handleInputChange}
                  placeholder="Enter lead name"
                  isInvalid={!!errors.name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>
            </div>
            <div className="col-md-3">
              <Form.Group controlId="Source">
                <Form.Label>Source</Form.Label>
                <Form.Control
                  type="text"
                  name="source"
                  value={newLead.source}
                  onChange={handleInputChange}
                  placeholder="Enter lead source"
                  isInvalid={!!errors.source}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.source}
                </Form.Control.Feedback>
              </Form.Group>
            </div>
            <div className="col-md-3">
              <Form.Group controlId="leadEmail">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={newLead.email}
                  onChange={handleInputChange}
                  placeholder="Enter lead email"
                  isInvalid={!!errors.email}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              </Form.Group>
            </div>
            <div className="col-md-2">
              <Form.Group controlId="followUpDate">
                <Form.Label>Follow-Up Date</Form.Label>
                <Form.Control
                  type="date"
                  name="followUpDate"
                  value={newLead.followUpDate}
                  onChange={handleInputChange}
                  isInvalid={!!errors.followUpDate}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.followUpDate}
                </Form.Control.Feedback>
              </Form.Group>
            </div>
            <div className="col-md-1 d-flex flex-nowrap text-nowrap align-items-end">
              <Button className="gradient_one" onClick={addLead}>
                {leadToEdit ? "Update" : "Add Lead"}
              </Button>
            </div>
          </div>
        </Form>
      </div>

      {/* Leads Table */}
      <div>
        <h4>Lead List</h4>
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Email</th>
              <th>Status</th>
              <th>Source</th>
              <th>Follow-Up Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((lead) => (
              <tr key={lead.id}>
                <td>{lead.id}</td>
                <td>{lead.name}</td>
                <td>{lead.email}</td>
                <td>{lead.status}</td>
                <td>{lead.source}</td>
                <td>{lead.followUpDate}</td>
                <td>
                  <Button
                    size="sm"
                    className="me-2 btn-secondary"
                    onClick={() => handleEdit(lead)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => confirmDelete(lead)}
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Confirmation Modal */}
      {showModal && (
        <ConfirmationModal
          showModal={showModal}
          title={"Do you want to delete the lead"}
          closeModal={closeModal}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
};

export default LeadManagement;
